<template>
  <UILoadingScreen :loading="inFlight"></UILoadingScreen>
  <section v-show="sizingForm.data" ref="wrapper">
    <section
      id="scenario-header-wrapper"
      ref="header"
      class="wrapper"
      :class="{
        'shadow-4 shrink': shouldHeaderShrink,
        'relative shadow-2': !shouldHeaderShrink
      }"
    >
      <div
        class="scenario-header noprint"
        :class="{ container: !shouldHeaderShrink }"
      >
        <div class="tabs">
          <router-link
            v-for="(item, i) in menuItems"
            :key="item.label"
            :to="item.to"
            :class="{ active: i === activeMenuIndex, disabled: item.disabled }"
          >
            <div v-if="locationOverride(item)" class="menu-item">Location</div>
            <div v-else class="menu-item">{{ item.label }}</div>
          </router-link>
        </div>

        <!-- SCENARIO SELECTOR -->
        <div class="scenarios">
          <div class="flex">
            <ValidationDropdown />
            <div class="scenario-switcher-wrap">
              <Button
                type="button"
                class="dropdown-sm p-inputtext-sm justify-content-between text-left scenario-switcher-trigger"
                :aria-expanded="scenarioPanelOpen"
                @click="toggleScenarioPanel($event)"
              >
                <span
                  class="scenario-switcher-trigger-label flex align-items-center min-w-0 flex-grow-1"
                  :title="scenarioTitle(scenarioId) ?? ''"
                >
                  {{ scenarioTitle(scenarioId) || "" }}
                  <Badge
                    v-if="
                      scenarioId ===
                      sizingForm.data.value?.installed_scenario_id
                    "
                    class="p-badge-installed p-badge-installed-full ml-2"
                    value="Installed"
                  />
                  <span
                    v-if="
                      scenarioId ===
                      sizingForm.data.value?.installed_scenario_id
                    "
                    class="p-badge-installed-short ml-2 flex-shrink-0"
                    title="Installed"
                  >
                    <icon :icon="['fas', 'info-circle']" />
                  </span>
                </span>
                <i
                  class="pi pi-chevron-down flex-shrink-0 ml-2"
                  aria-hidden="true"
                />
              </Button>
              <OverlayPanel
                ref="scenarioPanelRef"
                class="scenario-switcher-overlay"
                append-to="body"
                :dismissable="true"
                @show="onScenarioPanelShow"
                @hide="onScenarioPanelHide"
              >
                <div class="scenario-reorder-panel">
                  <div class="scenario-reorder-list">
                    <div
                      v-for="(s, idx) in panelScenarioRows"
                      :key="s.id"
                      class="scenario-reorder-row scenario-reorder-row-scenario"
                      :data-scenario-id="s.id"
                      :class="{
                        'is-dragging': dragActiveId === s.id,
                        'is-hover-target':
                          dropHoverId === s.id &&
                          dragActiveId &&
                          dragActiveId !== s.id
                      }"
                    >
                      <span
                        v-if="!isScenarioReadonly && !isImportedFromLegacy"
                        class="drag-handle-grip"
                        aria-label="Reorder scenario"
                        @pointerdown="onGripPointerDown($event, s.id)"
                      >
                        <span v-for="n in 6" :key="n" class="drag-handle-dot" />
                      </span>
                      <span
                        class="scenario-reorder-label flex-grow-1 cursor-pointer"
                        :title="s.title ?? undefined"
                        @click="navigateToScenarioChoice(String(s.id))"
                      >
                        {{ s.title }}
                      </span>
                      <span
                        v-if="idx === panelScenarioRows.length - 1"
                        class="text-color-secondary text-xs mr-2 flex-shrink-0"
                      >
                        Default
                      </span>
                      <Badge
                        v-if="
                          s.id === sizingForm.data.value?.installed_scenario_id
                        "
                        value="Installed"
                        class="p-badge-installed ml-2 flex-shrink-0"
                      />
                    </div>
                  </div>
                  <div class="scenario-reorder-actions">
                    <div
                      v-if="scenarioId !== 'new'"
                      class="scenario-reorder-row scenario-reorder-row-action"
                      @click="navigateToScenarioChoice('clone')"
                    >
                      Clone Current Scenario...
                    </div>
                    <div
                      class="scenario-reorder-row scenario-reorder-row-action"
                      @click="navigateToScenarioChoice('new')"
                    >
                      Create New Scenario...
                    </div>
                  </div>
                </div>
              </OverlayPanel>
            </div>
          </div>
        </div>

        <!-- Toggles/Diff/Save -->
        <div class="flex gap-1 align-items-center toggle">
          <FAToggleButton
            v-model="currentScenarioChartsOpen"
            :disabled="route.name === 'PerformanceEvaluation'"
          >
            <icon :icon="['fas', 'chart-line']" />
          </FAToggleButton>

          <FAToggleButton
            v-model="currentScenarioSideCalcsOpen"
            :disabled="
              route.name === 'PerformanceEvaluation' ||
              route.name === 'ScenarioSchematic'
            "
          >
            <icon :icon="['fas', 'calculator']" />
          </FAToggleButton>

          <fieldset
            class="flex align-items-stretch justify-content-end"
            :disabled="isScenarioReadonly || isImportedFromLegacy"
          >
            <span v-if="!!user.has_elevated_privileges">
              <Button
                class="ml-2 p-button-sm p-button-info"
                label="Show Diff"
                :disabled="!isDirty"
                @click="showDiff = true"
              />
            </span>
            <Button
              class="ml-2 p-button-sm p-button-info"
              label="Undo"
              :disabled="!isDirty || isNewScenario"
              @click="resetForms"
            />
            <Button
              class="ml-2 mr-2 p-button-sm p-button-info"
              label="Save"
              :disabled="!isDirty || isInvalidForm"
              @click="handleSave"
            />
          </fieldset>
        </div>
      </div>

      <ExistingMotorShroudBanner />

      <!-- DEV NOTE: Dual stack legend: only shows for split equipment view after scrolling past header -->
      <div
        v-if="dualLegendVisible && isSizingParametersRoute && hasYTool"
        class="dual-stack-legend noprint"
      >
        <span class="legend-pill upper-pill">Upper Stack (Left)</span>
        <span class="legend-pill lower-pill">Lower Stack (Right)</span>
      </div>

      <div class="container flex text-gray-900 font-light text-xs noprint">
        <p v-if="scenarioForm.data.value.author">
          Created By
          {{
            scenarioForm.data.value.author.display_name ||
            scenarioForm.data.value.author.email
          }}
        </p>
        <p v-if="scenarioForm.data.value.journal_updated_at" class="ml-4">
          Saved
          {{
            format(
              new Date(scenarioForm.data.value.journal_updated_at),
              "MMMM-dd-yyyy HH:mm:ss"
            )
          }}
        </p>
        <p v-if="isImportedFromLegacy" class="ml-4 font-bold text-danger-500">
          You cannot edit this scenario since it was imported from legacy
          reaver. Please clone the sizing so it is recreated correctly in the
          new system.
        </p>
        <p
          v-else-if="isScenarioReadonly"
          class="ml-4 font-bold text-danger-500"
        >
          You cannot edit this scenario since you are not the owner. Please
          clone the scenario so you own a copy before making any changes.
        </p>
      </div>

      <transition name="horizontal-slide">
        <ChartSwitcher
          v-if="currentScenarioChartsOpen && !isPerfEval"
          :class="{ container: !shouldHeaderShrink }"
          :espOnShore="sizingForm?.data.value.pumping_type"
          class="py-2"
        />
      </transition>
    </section>
    <Splitter
      v-if="!pageTooSmallToSplit && !isPerfEval && !isSchematic"
      ref="splitter"
      state-storage="local"
      state-key="scenario-splitter"
      :gutter-size="6"
    >
      <SplitterPanel :minSize="35" :size="80">
        <div :id="SPLITTER_LEFT_ID" ref="splitterLeft">
          <router-view v-slot="{ Component }">
            <keep-alive>
              <component
                :is="Component"
                :key="`${String(route.name)}-${scenarioId}-${perfevalId}`"
                :class="{
                  'only-rows': splitterLeftRect
                    ? splitterLeftRect.width < 650
                    : false
                }"
                @scenario-delete-flow="() => (isScenarioDeleteFlow = true)"
              />
            </keep-alive>
          </router-view>
        </div>
      </SplitterPanel>
      <SplitterPanel
        v-show="currentScenarioSideCalcsOpen"
        id="rightSplitterPanel"
        :size="20"
        :min-size="15"
      >
        <div
          id="splitter-right"
          ref="splitterRight"
          class="splitter-right"
          :style="rightSplitterStyle"
        >
          <EquipmentCalculator
            v-if="scenarioCalculations"
            :router-params="routeParams"
            multi-select
          />
        </div>
      </SplitterPanel>
    </Splitter>
    <div v-else-if="isPerfEval">
      <router-view v-slot="{ Component }">
        <component
          :is="Component"
          v-if="isPerfEval"
          :key="`${String(route.name)}-${scenarioId}-${perfevalId}`"
          :class="{
            'only-rows': pageTooSmallToSplit
          }"
        />
      </router-view>
    </div>
    <div v-else-if="isSchematic" class="schematic-wrapper">
      <router-view v-slot="{ Component }">
        <component
          :is="Component"
          :key="`${String(route.name)}-${scenarioId}-${perfevalId}`"
          :class="{
            'only-rows': pageTooSmallToSplit
          }"
        />
      </router-view>
    </div>
    <div v-else>
      <router-view v-slot="{ Component }">
        <keep-alive>
          <component
            :is="Component"
            :key="`${String(route.name)}-${scenarioId}-${perfevalId}`"
            :class="{
              'only-rows': pageTooSmallToSplit
            }"
            @scenario-delete-flow="() => (isScenarioDeleteFlow = true)"
          />
        </keep-alive>
      </router-view>
      <div class="container">
        <EquipmentCalculator
          v-if="scenarioCalculations"
          :router-params="routeParams"
          multi-select
        />
      </div>
    </div>

    <Dialog
      v-model:visible="showDiff"
      header="Form Diff"
      :draggable="false"
      :style="{ width: '60vw' }"
      modal
    >
      <div class="p-4">
        <vue-json-pretty
          :data="{
            sizing: sizingForm.diff.value,
            scenario: scenarioForm.diff.value,
            equipment: equipmentForm.diff.value,
            parameters: parametersForm.diff.value,
            wellProperties: wellPropertiesForm.diff.value,
            correlations: correlationsForm.diff.value,
            deviationSurvey: deviationSurveyForm.diff.value,
            seals: sealsForm.diff.value,
            intakes: intakesForm.diff.value,
            cables: cablesForm.diff.value,
            pumps: pumpsForm.diff.value,
            housings: housingsForm.diff.value,
            yToolBypassTubings: yToolBypassTubingsForm.diff.value,
            yToolEquipmentTubings: yToolEquipmentTubingsForm.diff.value
          }"
        ></vue-json-pretty>
      </div>
    </Dialog>
  </section>
  <CloneDialog
    v-if="showConfirmCloneScenario"
    v-model:should-clone="showConfirmCloneScenario"
    type="Scenario"
    @save-clone="handleCloneScenario"
  />
</template>

<script setup lang="ts">
import type {
  Deviation_Survey_Insert_Input,
  InputMaybe,
  Revisions_Scenario_Equipment_Revisions_Insert_Input,
  Revisions_Scenario_Equipment_Xref_Pumps_Revisions_Insert_Input
} from "gql/api";
import { Sizing_Pumping_Type_Enum, useSizingsManifoldMutation } from "gql/api";
import {
  computed,
  inject,
  provide,
  ref,
  watch,
  onMounted,
  onUnmounted,
  nextTick
} from "vue";
import type {
  NavigationGuard,
  RouteLocationNormalized,
  RouteParams,
  RouteQueryAndHash,
  RouteRecordName
} from "vue-router";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { useWindowScroll, useOnline } from "@vueuse/core";
import Splitter from "primevue/splitter";
import SplitterPanel from "primevue/splitterpanel";
import Badge from "primevue/badge";
import FAToggleButton from "@/components/FAToggleButton.vue";
import {
  haltUntilCompletion,
  inFlight
} from "@/composables/exchanges/mutationLimiter";
import {
  injectStrict,
  isEmptyObject,
  omit,
  toggleObservable
} from "@/utils/utils";
import {
  jsonToPostgresArray,
  restoreRefsOnMutationError
} from "@/utils/utils-dependent";
import EquipmentCalculator from "@/components/scenarios/calculator_sections/EquipmentCalculator.vue";
import ChartSwitcher from "@/components/scenarios/charts/ChartSwitcher.vue";
import { useScenarioDetailProvider } from "@/composables/scenarioDetailProvider/scenarioDetailProvider";
import ValidationDropdown from "@/components/scenarios/ValidationDropdown.vue";
import ExistingMotorShroudBanner from "@/components/scenarios/ExistingMotorShroudBanner.vue";
import { useLogger } from "@/composables/logger";
import useEmptyScenario from "@/composables/useEmptyScenario";
import exportScenario from "@/utils/exports/exportScenario";
import useImportScenario from "@/composables/useImportScenario";

import { format } from "date-fns";
import {
  CalculatorDic,
  perfevalsKey,
  scenariosKey,
  sizingFormKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { buildInfoKey } from "@/injectionKeys";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import type {
  Scenario,
  Correlations,
  ScenarioDetailForms
} from "@/composables/forms/formTypes";
import { useWipePerfEvals } from "@/composables/useWipePerfEvals";
import { useMutationNotification } from "@/composables/logger/mutationNotification";
import UILoadingScreen from "@/components/UILoadingScreen.vue";
import CloneDialog from "@/components/CloneDialog.vue";
import useUserInfo from "@/composables/auth/useUserInfo";
import { discardConfirmationKey } from "@/plugins/confirmationPlugin";
import { useSettingsStore } from "@/store";
import { orderScenariosBySizingOrder } from "@/utils/scenarioUtils";

const props = defineProps<{
  sizingId: string;
  scenarioId: string;
  perfevalId: string;
  routeName: RouteRecordName | null | undefined;
  routeParams: RouteParams;
  routeQuery: RouteQueryAndHash;
}>();

defineEmits<{
  (e: "destructive-action", next: () => void): void;
}>();

const { flags, currentScenarioSideCalcsOpen, currentScenarioChartsOpen } =
  useSettingsStore();
const { warn } = useLogger("ScenarioDetail");
const route = useRoute();
const router = useRouter();
const showDiff = ref(false);
const isCloneDialogOpen = ref(false);
const { executeMutation } = useSizingsManifoldMutation();
const user = useUserInfo();
const scenarioPanelRef = ref<{
  toggle: (e: Event) => void;
  hide?: () => void;
}>();
const scenarioPanelOpen = ref(false);
const localScenarioOrder = ref<string[] | null>(null);
const dragPreviewOrder = ref<string[] | null>(null);
const dragActiveId = ref<string | null>(null);
const dropHoverId = ref<string | null>(null);

const showConfirmCloneScenario = ref(false);
const sizingForm = injectStrict(sizingFormKey);
const isNewScenario = computed(() => route.params.scenarioId === "new");

const isPerfEval = computed(() => route.name === "PerformanceEvaluation");
const isSchematic = computed(() => route.name === "ScenarioSchematic");
const isSizingParametersRoute = computed(
  () => route.name === "SizingParameters"
);
// Computes data and provides to children
// via provide/inject
const scenarios = injectStrict(scenariosKey);
const sizing = injectStrict(sizingKey);
const scenario = computed(() =>
  scenarios.value.find(s => s.id === route.params.scenarioId)
);
const orderedScenarios = computed(() =>
  orderScenariosBySizingOrder<Scenario>(
    {
      scenario_order:
        localScenarioOrder.value ||
        (sizingForm.data.value as { scenario_order?: string[] | null })
          ?.scenario_order ||
        []
    },
    scenarios.value ?? []
  )
);
const orderedScenarioIds = computed(() =>
  orderedScenarios.value.flatMap(item => (item?.id ? [item.id] : []))
);

const panelScenarioRows = computed(() => {
  const ids = dragPreviewOrder.value ?? orderedScenarioIds.value;
  const byId = new Map((scenarios.value ?? []).map(s => [s.id, s] as const));
  return ids.map(id => byId.get(id)).filter((s): s is Scenario => !!s);
});

const isHps = computed(() => {
  return sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});
const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const perfevals = computed(
  () => scenario.value?.r_performance_evaluations || []
);

const { deleteAllPerfEvalsPrompt } = useWipePerfEvals(perfevals);

provide(perfevalsKey, perfevals);
const {
  scenarioForm,
  equipmentForm,
  parametersForm,
  wellPropertiesForm,
  deviationSurveyForm,
  sealsForm,
  intakesForm,
  cablesForm,
  pumpsForm,
  housingsForm,
  yToolBypassTubingsForm,
  yToolEquipmentTubingsForm,
  splitYToolBypassTubings,
  splitYToolEquipmentTubings,
  prepareSplitYToolBypassTubingsDiffs,
  prepareSplitYToolEquipmentTubingsDiffs,
  correlationsForm,
  scenarioCalculations
} = useScenarioDetailProvider(scenario);

const hasYTool = computed(() => {
  const eq = equipmentForm.data.value;
  return eq.use_uphole_y_tool || eq.use_downhole_y_tool;
});

const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions(
  sizingForm,
  scenarioForm
);

const createEmptyScenario = useEmptyScenario(sizingForm.data);

const isScenarioDeleteFlow = ref(false);
const isScenarioCloneFlow = ref(false);
const cloneImportInFlight = ref(false);

const setEmptyScenario = () => {
  const emptyScenario = createEmptyScenario();
  scenarioForm.update(emptyScenario);
  if (
    emptyScenario.equipment &&
    emptyScenario.parameters &&
    emptyScenario.well_properties &&
    emptyScenario.correlations
  ) {
    equipmentForm.update(emptyScenario.equipment);
    parametersForm.update(emptyScenario.parameters);
    wellPropertiesForm.update(emptyScenario.well_properties);
    correlationsForm.update(
      emptyScenario.correlations as NonNullable<Correlations>
    );
  }
};

if (isNewScenario.value) {
  setEmptyScenario();
}

// Ensure that the new scenario's display unit overrides are set when
// updating the global sizing unit system.
watch(
  () => sizingForm.data.value.default_display_units,
  (newVal, oldVal) => {
    if (isNewScenario.value && newVal !== oldVal) {
      const emptyScenario = createEmptyScenario();
      scenarioForm.update({
        display_unit_overrides: emptyScenario.display_unit_overrides
      });
    }
  }
);

const activeMenuIndex = computed(() =>
  menuItems.value.findIndex(item => item.to.name === route.name)
);

const locationOverride = (item: { label: string; to: { name: string } }) =>
  isHps.value && item.to.name === "WellInfo";

const menuItems = computed(() => {
  const baseMenuItems = [
    {
      label: "Info",
      to: {
        name: "ScenarioInfo",
        params: {
          sizingId: props.sizingId,
          scenarioId: props.scenarioId
        }
      }
    },
    {
      label: "Well",
      to: {
        name: "WellInfo",
        params: {
          sizingId: props.sizingId,
          scenarioId: props.scenarioId
        }
      },
      disabled: isNewScenario.value
    },
    {
      label: "Parameters",
      to: {
        name: "SizingParameters",
        params: {
          sizingId: props.sizingId,
          scenarioId: props.scenarioId
        }
      },
      disabled: isNewScenario.value
    }
  ];
  const espMenuItems = [
    {
      label: "Schematic",
      to: {
        name: "ScenarioSchematic",
        params: {
          sizingId: props.sizingId,
          scenarioId: props.scenarioId
        }
      },
      disabled: isNewScenario.value
    },
    {
      label: "Analysis",
      to: {
        name: "ScenarioAnalysis",
        params: {
          sizingId: props.sizingId,
          scenarioId: props.scenarioId
        }
      },
      disabled: isNewScenario.value
    },
    {
      label: "Evaluations",
      to: {
        name: "PerformanceEvaluation",
        params: {
          sizingId: props.sizingId,
          scenarioId: props.scenarioId,
          perfevalId: scenario.value?.r_performance_evaluations[0]?.id || "new"
        }
      },
      disabled: isNewScenario.value
    }
  ];
  if (isEspOnShore.value) {
    return [...baseMenuItems, ...espMenuItems];
  }
  return baseMenuItems;
});

provide(CalculatorDic, ref({}));

// Set refs on page mount to read from user settings
function setDefaultsFromUserSettings(): void {
  currentScenarioChartsOpen.value = flags.value.openPumpTableByDefault;
  currentScenarioSideCalcsOpen.value = flags.value.openSideCalcsByDefault;
}

// header scroll tracking for dynamic sticky header
const { y: pageScrollY } = useWindowScroll();
const shouldHeaderShrink = computed(() => pageScrollY.value > 100);

const wrapper = ref(); // DOM ref to the entire ScenarioDetail page
const header = ref<Element>(); // DOM ref to the header section above the splitter
const headerRect = ref<DOMRectReadOnly>(); // resize observer will fill this out
const splitter = ref(); // DOM ref to the splitter wrapper
const splitterLeft = ref<Element>(); // DOM ref to splitter's left side wrapper
const splitterLeftRect = ref<DOMRectReadOnly>(); // resize observer fills this out
const splitterRight = ref(); // DOM ref to the splitter's right side wrapper

const SPLITTER_LEFT_ID = ref("splitter-left");

const resizeObserver = new ResizeObserver(entries => {
  headerRect.value =
    entries.find(e => e?.target?.classList?.contains("wrapper"))?.contentRect ??
    headerRect.value;

  splitterLeftRect.value =
    entries.find(e => e?.target?.id === SPLITTER_LEFT_ID.value)?.contentRect ??
    splitterLeftRect.value;
});

watch(header, (newV, oldV) => {
  toggleObservable(resizeObserver, newV, oldV);
  if (!newV) headerRect.value = undefined;
});

watch(splitterLeft, (newV, oldV) => {
  toggleObservable(resizeObserver, newV, oldV);
  if (!newV) splitterLeftRect.value = undefined;
});

onMounted(() => {
  setDefaultsFromUserSettings();
  header.value && resizeObserver.observe(header.value);
  splitterLeft.value && resizeObserver.observe(splitterLeft.value);
});

onUnmounted(() => {
  header.value && resizeObserver.unobserve(header.value);
  splitterLeft.value && resizeObserver.unobserve(splitterLeft.value);
  setDefaultsFromUserSettings();
  teardownReorderListeners();
  if (dragActiveId.value) {
    endDragSession(false);
  }
});

const pageTooSmallToSplit = computed(
  () => (headerRect.value?.width ?? 650) < 650
);

const rightSplitterStyle = computed(() => {
  return {
    top: `calc(${headerRect.value?.bottom ?? 0}px)`,
    height: `calc(100vh - ${headerRect.value?.bottom ?? 0 + 65}px)`
  };
});

const dualLegendVisible = ref(false);

const updateDualLegendVisibility = () => {
  if (!isSizingParametersRoute.value || !hasYTool.value || !headerRect.value) {
    dualLegendVisible.value = false;
    return;
  }

  const splitHeaderEl = document.querySelector(
    ".split-equipment-view .split-header"
  ) as HTMLElement | null;

  if (!splitHeaderEl) {
    dualLegendVisible.value = false;
    return;
  }

  const rect = splitHeaderEl.getBoundingClientRect();
  const threshold = headerRect.value.bottom;

  dualLegendVisible.value = rect.top <= threshold;
};

watch(
  [
    pageScrollY,
    isSizingParametersRoute,
    hasYTool,
    () => headerRect.value?.bottom
  ],
  updateDualLegendVisibility,
  { immediate: true }
);

const isInvalidForm = computed(() =>
  [
    sizingForm,
    scenarioForm,
    equipmentForm,
    parametersForm,
    wellPropertiesForm,
    deviationSurveyForm,
    sealsForm,
    intakesForm,
    cablesForm,
    pumpsForm,
    housingsForm,
    correlationsForm
  ].some(f => {
    return f.V?.value.$invalid;
  })
);

const dirtyForms = computed(() => {
  const forms: ScenarioDetailForms = {
    sizingForm,
    scenarioForm,
    equipmentForm,
    parametersForm,
    wellPropertiesForm,
    deviationSurveyForm,
    sealsForm,
    intakesForm,
    cablesForm,
    pumpsForm,
    housingsForm,
    yToolBypassTubingsForm,
    yToolEquipmentTubingsForm,
    correlationsForm
  };
  // Check if any y-tool forms have changes
  const yToolBypassDirty = !isEmptyObject(yToolBypassTubingsForm.diff.value);
  const yToolEquipmentDirty = !isEmptyObject(
    yToolEquipmentTubingsForm.diff.value
  );

  const dirtyKeys = Object.keys(forms).filter(k => {
    const formKey = k as keyof ScenarioDetailForms;
    // Skip y-tool forms in initial filter, we'll add them conditionally
    if (
      formKey === "yToolBypassTubingsForm" ||
      formKey === "yToolEquipmentTubingsForm"
    ) {
      return false;
    }
    return !isEmptyObject(forms[formKey].diff.value);
  });

  // Add y-tool forms if any are dirty
  if (yToolBypassDirty) {
    dirtyKeys.push("yToolBypassTubingsForm");
  }
  if (yToolEquipmentDirty) {
    dirtyKeys.push("yToolEquipmentTubingsForm");
  }

  return dirtyKeys;
});

const isDirty = computed(() => dirtyForms.value.length > 0);

/**
 * Handle alerting the user when they are making browser-level navigation.
 * It's wonky but works most of the time in Chrome.
 */
const onBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  const msg =
    "Are you sure you want to leave? You have pending changes that will be lost.";

  if (isDirty.value) {
    return (event.returnValue = msg);
  }
};

window.addEventListener("beforeunload", onBeforeUnload, { capture: true });

onUnmounted(() => {
  window.removeEventListener("beforeunload", onBeforeUnload, { capture: true });
});

const routeChangeRequiresDiscardConfirmation = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): boolean => {
  if (!isDirty.value) return false;
  if (!from.fullPath.startsWith("/sizings/")) {
    return false;
  }

  const onlySizingFormDiffers =
    dirtyForms.value.length === 1 && dirtyForms.value[0] === "sizingForm";
  const sameSizing = to.params.sizingId === from.params.sizingId;
  const sameScenario = to.params.scenarioId === from.params.scenarioId;

  const lastScenario = orderedScenarios.value?.length
    ? orderedScenarios.value[orderedScenarios.value.length - 1]
    : null;

  const defaultingScenario =
    sameSizing &&
    to.params.scenarioId === lastScenario?.id &&
    !sizing.value?.installed_scenario_id &&
    to.fullPath.endsWith("/well");

  if (defaultingScenario) {
    return false;
  }

  if (sameSizing && onlySizingFormDiffers) return false;

  if (isScenarioDeleteFlow.value) return false;
  if (isScenarioCloneFlow.value) return false;
  if (to.query.deletedScenario) return false;

  if (
    from.fullPath.startsWith(to.fullPath) &&
    to.fullPath.startsWith("/sizings/")
  )
    return false;

  if (from.fullPath.startsWith("/sizings/new/") && isSaving.value) {
    return false;
  }

  if (
    sameSizing &&
    sameScenario &&
    to.name !== "Reports" &&
    to.name !== "PerformanceEvaluation"
  ) {
    return false;
  }
  return true;
};
const { toggleDiscardChanges } = injectStrict(discardConfirmationKey);

const handleRouteChange: NavigationGuard = async (to, from) => {
  if (routeChangeRequiresDiscardConfirmation(to, from)) {
    return toggleDiscardChanges();
  }
  return true;
};
onBeforeRouteLeave(handleRouteChange);

const resetForms = () => {
  localScenarioOrder.value = null;
  sizingForm.reset();
  scenarioForm.reset();
  equipmentForm.reset();
  parametersForm.reset();
  wellPropertiesForm.reset();
  deviationSurveyForm.reset();
  sealsForm.reset();
  intakesForm.reset();
  cablesForm.reset();
  pumpsForm.reset();
  housingsForm.reset();
  yToolBypassTubingsForm.reset();
  yToolEquipmentTubingsForm.reset();
  correlationsForm.reset();
};

watch([() => route.params.scenarioId, () => route.params.sizingId], () => {
  resetForms();
  if (isNewScenario.value) {
    setEmptyScenario();
  }
});

const importScenario = useImportScenario();

const buildInfo = inject(buildInfoKey);

async function handleCloneScenario(title: string) {
  if (cloneImportInFlight.value) return;
  cloneImportInFlight.value = true;
  isScenarioCloneFlow.value = true;
  try {
    if (!buildInfo || !scenarioForm.data.value) return;
    // Need to reconstruct object from all forms' data so the new cloned scenario
    // has the latest changes from the user
    const latestFormData: Scenario = {
      ...scenarioForm.data.value,
      well_properties: { ...wellPropertiesForm.data.value },
      parameters: { ...parametersForm.data.value },
      correlations: { ...correlationsForm.data.value },
      equipment: {
        ...equipmentForm.data.value,
        r_xref_cables: [...Object.values(cablesForm.data.value)],
        r_xref_intakes: [...Object.values(intakesForm.data.value)],
        r_xref_seals: [...Object.values(sealsForm.data.value)],
        r_xref_pumps:
          equipmentForm.data.value?.pumps_order?.map((pid: string) => ({
            ...pumpsForm.data.value[pid],
            r_xref_housings:
              pumpsForm.data.value[pid]?.housings_order?.map((hid: string) => ({
                ...housingsForm.data.value[hid]
              })) || []
          })) || []
      }
    };
    const { snapshot } = exportScenario(buildInfo, latestFormData);
    if (!snapshot.payload) {
      warn("exported scenario doesn't have a payload");
      return;
    }

    snapshot.payload.title = title;
    const importedScenario = await importScenario(snapshot);

    isCloneDialogOpen.value = false;
    showConfirmCloneScenario.value = false;

    await router.push({
      name: "WellInfo",
      params: {
        sizingId: props.sizingId,
        scenarioId: importedScenario?.id
      }
    });
  } finally {
    cloneImportInFlight.value = false;
    isScenarioCloneFlow.value = false;
  }
}

const { registerResultToast } = useMutationNotification();

const isSaving = ref(false);
const ignoredSizingFields = [
  "parent_sizing",
  "author",
  "well",
  "customer",
  "scenarios"
];

async function handleSave() {
  isSaving.value = true;
  try {
    await onSave();
  } catch (err) {}
  isSaving.value = false;
}

async function onSave() {
  // HACK
  // when saving a new scenario revision, we want to strip off all of our
  // xref data (anything in another table)
  const sizingObject = omit(sizingForm.prepare(), (_, k) =>
    ignoredSizingFields.includes(k)
  );
  const sizingRevisions = isEmptyObject(sizingForm.diff.value)
    ? []
    : [sizingObject];

  const ignoredScenarioFields = [
    "author",
    "correlations",
    "equipment",
    "parameters",
    "well_properties"
  ];

  const scenarioObject = omit(scenarioForm.prepare(), (_, k) =>
    ignoredScenarioFields.includes(k)
  );
  const scenarioRevisions = isEmptyObject(scenarioForm.diff.value)
    ? []
    : [scenarioObject];

  // remove ids from x_order if corresponding xrefs don't exist
  const equipmentObject: Revisions_Scenario_Equipment_Revisions_Insert_Input = {
    ...equipmentForm.prepare(),
    seals_order: jsonToPostgresArray(
      equipmentForm.data.value.seals_order?.filter((sealId: string) =>
        Object.keys(sealsForm.data.value).includes(sealId)
      ) || null
    ) as InputMaybe<string[]>,
    cables_order: jsonToPostgresArray(
      equipmentForm.data.value.cables_order?.filter((cableId: string) =>
        Object.keys(cablesForm.data.value).includes(cableId)
      ) || null
    ) as InputMaybe<string[]>,
    pumps_order: jsonToPostgresArray(
      equipmentForm.data.value.pumps_order?.filter((pumpId: string) =>
        Object.keys(pumpsForm.data.value).includes(pumpId)
      ) || null
    ) as InputMaybe<string[]>,
    intakes_order: jsonToPostgresArray(
      equipmentForm.data.value.intakes_order?.filter((intakeId: string) =>
        Object.keys(intakesForm.data.value).includes(intakeId)
      ) || null
    ) as InputMaybe<string[]>,
    uphole_y_tool_bypass_tubing_order: jsonToPostgresArray(
      equipmentForm.data.value.uphole_y_tool_bypass_tubing_order?.filter(
        (tubingId: string) =>
          Object.keys(
            splitYToolBypassTubings(yToolBypassTubingsForm.data.value).uphole
          ).includes(tubingId)
      ) || null
    ) as InputMaybe<string[]>,
    uphole_y_tool_equipment_tubing_order: jsonToPostgresArray(
      equipmentForm.data.value.uphole_y_tool_equipment_tubing_order?.filter(
        (tubingId: string) =>
          Object.keys(
            splitYToolEquipmentTubings(yToolEquipmentTubingsForm.data.value)
              .uphole
          ).includes(tubingId)
      ) || null
    ) as InputMaybe<string[]>,
    downhole_y_tool_bypass_tubing_order: jsonToPostgresArray(
      equipmentForm.data.value.downhole_y_tool_bypass_tubing_order?.filter(
        (tubingId: string) =>
          Object.keys(
            splitYToolBypassTubings(yToolBypassTubingsForm.data.value).downhole
          ).includes(tubingId)
      ) || null
    ) as InputMaybe<string[]>,
    downhole_y_tool_equipment_tubing_order: jsonToPostgresArray(
      equipmentForm.data.value.downhole_y_tool_equipment_tubing_order?.filter(
        (tubingId: string) =>
          Object.keys(
            splitYToolEquipmentTubings(yToolEquipmentTubingsForm.data.value)
              .downhole
          ).includes(tubingId)
      ) || null
    ) as InputMaybe<string[]>
  };

  const equipmentRevisions = isEmptyObject(equipmentForm.diff.value)
    ? []
    : [equipmentObject];

  const parametersObject = parametersForm.prepare();

  const parametersRevisions = isEmptyObject(parametersForm.diff.value)
    ? []
    : [parametersObject];

  const wellPropertiesObject = wellPropertiesForm.prepare();

  const wellPropertiesRevisions = isEmptyObject(wellPropertiesForm.diff.value)
    ? []
    : [wellPropertiesObject];

  const correlationsObject = correlationsForm.prepare();
  const correlationsRevisions = isEmptyObject(correlationsForm.diff.value)
    ? []
    : [correlationsObject];

  const deviationSurveyObject: Deviation_Survey_Insert_Input = {
    ...deviationSurveyForm.prepare(),
    entry_rows: jsonToPostgresArray(
      deviationSurveyForm.data.value?.entry_rows || null
    ) as InputMaybe<string[]>
  };

  const deviationSurveyObjects =
    !wellPropertiesForm.diff.value?.deviation_survey_id ||
    isEmptyObject(deviationSurveyForm.diff.value)
      ? []
      : [deviationSurveyObject];

  // regardless of revisions we're sending, massage pumps' housings orders into the right format
  const pumps = pumpsForm
    .prepareDiffsAsArray()
    .map(
      (
        pump: Revisions_Scenario_Equipment_Xref_Pumps_Revisions_Insert_Input
      ) => {
        return {
          ...pump,
          housings_order: jsonToPostgresArray(
            pump?.housings_order?.filter((housingId: string) =>
              Object.keys(housingsForm.data.value).includes(housingId)
            ) || null
          ) as InputMaybe<string[]>
        };
      }
    );

  // prompt to delete perf evals if anything other than sizing or scenario has diff
  const willDeletePerfEvalsOnSave = [
    equipmentForm,
    parametersForm,
    wellPropertiesForm,
    deviationSurveyForm,
    sealsForm,
    intakesForm,
    cablesForm,
    pumpsForm,
    housingsForm,
    yToolBypassTubingsForm,
    yToolEquipmentTubingsForm,
    correlationsForm
  ].some(f => !isEmptyObject(f.diff.value));

  if (willDeletePerfEvalsOnSave) {
    try {
      await deleteAllPerfEvalsPrompt();
    } catch (err) {
      return;
    }
  }

  // save relevant diffs
  const result = executeMutation({
    sizings_revisions: sizingRevisions,
    scenarios_revisions: scenarioRevisions,
    scenario_equipment_revisions: equipmentRevisions,
    scenario_parameters_revisions: parametersRevisions,
    scenario_well_properties_revisions: wellPropertiesRevisions,
    deviation_survey: deviationSurveyObjects,
    scenario_correlations_revisions: correlationsRevisions,
    scenario_equipment_xref_seals_revisions: sealsForm.prepareDiffsAsArray(),
    scenario_equipment_xref_intakes_revisions:
      intakesForm.prepareDiffsAsArray(),
    scenario_equipment_xref_cables_revisions: cablesForm.prepareDiffsAsArray(),
    scenario_equipment_xref_pumps_revisions: pumps,
    scenario_equipment_xref_housings_revisions:
      housingsForm.prepareDiffsAsArray(),
    sc_equip_xref_uphole_ytool_bypass_tubings_revisions:
      prepareSplitYToolBypassTubingsDiffs().uphole,
    sc_equip_xref_uphole_ytool_equip_tubings_revisions:
      prepareSplitYToolEquipmentTubingsDiffs().uphole,
    sc_equip_xref_downhole_ytool_bypass_tubings_revisions:
      prepareSplitYToolBypassTubingsDiffs().downhole,
    sc_equip_xref_downhole_ytool_equip_tubings_revisions:
      prepareSplitYToolEquipmentTubingsDiffs().downhole
  });

  restoreRefsOnMutationError(
    result,
    sizingForm.data,
    scenarioForm.data,
    equipmentForm.data,
    parametersForm.data,
    wellPropertiesForm.data,
    correlationsForm.data,
    deviationSurveyForm.data,
    sealsForm.data,
    intakesForm.data,
    cablesForm.data,
    pumpsForm.data,
    housingsForm.data,
    yToolBypassTubingsForm.data,
    yToolEquipmentTubingsForm.data
  );

  registerResultToast("Sizing Update", result);

  await haltUntilCompletion();

  // dont reroute if mutation returned an error
  const online = useOnline();
  if (online.value) {
    const { error } = await result;
    if (error) return;
  }
  if (isNewScenario.value) {
    await router.replace({
      name: "WellInfo",
      params: {
        sizingId: sizingForm.data.value.id,
        scenarioId: scenarioForm.data.value.id
      }
    });

    setEmptyScenario();
  }

  resetForms();
  // Reset forms after optimistic response from URQL
}

watch(showConfirmCloneScenario, newVal => {
  if (newVal) {
    scenarioPanelRef.value?.hide?.();
    const activeElement = document.activeElement as HTMLElement;
    activeElement?.blur();
  }
});

function teardownReorderListeners() {
  window.removeEventListener("pointermove", onReorderPointerMove);
  window.removeEventListener("pointerup", onReorderPointerUp);
  window.removeEventListener("pointercancel", onReorderPointerCancel);
}

function endDragSession(saveIfChanged: boolean) {
  teardownReorderListeners();
  const preview = dragPreviewOrder.value;
  const active = dragActiveId.value;
  dragActiveId.value = null;
  dropHoverId.value = null;
  dragPreviewOrder.value = null;
  if (saveIfChanged && preview && active) {
    const before = orderedScenarioIds.value.join(",");
    const after = preview.join(",");
    if (before !== after) {
      sizingForm.update({
        scenario_order: preview
      } as Partial<typeof sizingForm.data.value>);
    }
  }
}

function onReorderPointerMove(e: PointerEvent) {
  if (!dragActiveId.value || !dragPreviewOrder.value) return;
  const els = document.elementsFromPoint(e.clientX, e.clientY);
  const hit = els.find(
    el =>
      el instanceof HTMLElement &&
      el.classList.contains("scenario-reorder-row-scenario")
  ) as HTMLElement | undefined;
  const targetId = hit?.dataset.scenarioId;
  const sourceId = dragActiveId.value;
  if (!targetId || !sourceId || targetId === sourceId) {
    dropHoverId.value = targetId || null;
    return;
  }
  dropHoverId.value = targetId;
  const arr = [...dragPreviewOrder.value];
  const from = arr.indexOf(sourceId);
  const to = arr.indexOf(targetId);
  if (from < 0 || to < 0) return;

  const rect = hit.getBoundingClientRect();
  const insertAfterLast =
    to === arr.length - 1 && e.clientY > rect.top + rect.height / 2;

  arr.splice(from, 1);
  if (insertAfterLast) {
    arr.splice(arr.length, 0, sourceId);
  } else {
    // After removal, indices >= from shift; insert using target index in the original array.
    const insertAt = from < to ? to - 1 : to;
    arr.splice(insertAt, 0, sourceId);
  }
  dragPreviewOrder.value = arr;
}

function onReorderPointerUp() {
  endDragSession(true);
}

function onReorderPointerCancel() {
  endDragSession(false);
}

function onGripPointerDown(e: PointerEvent, id: string) {
  if (isScenarioReadonly.value || isImportedFromLegacy.value) return;
  e.preventDefault();
  e.stopPropagation();
  dragActiveId.value = id;
  dragPreviewOrder.value = [...orderedScenarioIds.value];
  window.addEventListener("pointermove", onReorderPointerMove);
  window.addEventListener("pointerup", onReorderPointerUp, { once: true });
  window.addEventListener("pointercancel", onReorderPointerCancel, {
    once: true
  });
}

function onScenarioPanelShow() {
  scenarioPanelOpen.value = true;
}

function onScenarioPanelHide() {
  scenarioPanelOpen.value = false;
  if (dragActiveId.value) {
    endDragSession(true);
  }
}

function toggleScenarioPanel(e: Event) {
  scenarioPanelRef.value?.toggle(e);
}

async function navigateToScenarioChoice(value: string) {
  scenarioPanelRef.value?.hide?.();
  const newScenario = scenarios.value.find(s => s.id === value);
  const continueChange = !isDirty.value || (await toggleDiscardChanges());

  if (!continueChange) return;

  if (value === "new") {
    await router.push({
      name: "ScenarioInfo",
      params: {
        scenarioId: "new"
      }
    });
    setEmptyScenario();
  } else if (value === "clone") {
    const activeElement = document.activeElement as HTMLElement;
    activeElement?.blur();
    await nextTick();
    setTimeout(() => {
      showConfirmCloneScenario.value = true;
    }, 50);
  } else {
    if (route.name === "PerformanceEvaluation" && newScenario) {
      await router.push({
        params: {
          scenarioId: value,
          perfevalId: newScenario.r_performance_evaluations[0]?.id || "new"
        }
      });
    } else {
      await router.push({ params: { scenarioId: value } });
    }
  }
}

const scenarioSwitcherOptions = computed(() => {
  const options = [
    ...orderedScenarios.value,
    { title: "Clone Current Scenario...", id: "clone" },
    { title: "Create New Scenario...", id: "new" }
  ];

  // don't show clone option on new scenario page
  if (props.scenarioId === "new") {
    options.splice(options.length - 2, 1);
  }

  return options;
});

function scenarioTitle(match: string) {
  if (scenarioSwitcherOptions.value?.length) {
    return scenarioSwitcherOptions.value.find(s => s?.id === match)?.title;
  }
  return "";
}

// Navigate to some perf eval if we are on the Perf Eval page with none selected
// because we don't want users to land on perf eval with nothing selected
if (!perfevals.value.map(perf => perf.id).includes(props.perfevalId)) {
  router.replace({
    params: {
      perfevalId: scenario.value?.r_performance_evaluations[0]?.id || "new"
    }
  });
}

// change loaded perfeval when one is deleted
watch(perfevals, (newPerfs, oldPerfs) => {
  if (oldPerfs.length > newPerfs.length) {
    router.replace({ params: { perfevalId: newPerfs[0]?.id || "new" } });
  }
});

defineExpose({
  onSave: handleSave,
  resetForms,
  isDirty: () => isDirty.value,
  isNewScenario: () => isNewScenario.value
});
</script>

<style lang="scss" scoped>
@media print {
  .wrapper {
    display: none;
  }
}

.wrapper {
  z-index: 2;
  position: relative;
  padding: 0;
  background-color: var(--gray-300);
  transition: all 0.2s ease-in-out;

  .scenario-header {
    position: relative;
    padding: 0.5em 0;
    justify-content: flex-start;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 0.25em;
    grid-auto-flow: dense;
    align-items: center;
    grid-auto-rows: auto;
    grid-template-areas:
      "tabs"
      "scenarios"
      "controls";

    @include bp("desktop") {
      grid-template-columns: 1fr 1fr;
      grid-template-areas: "tabs tabs" "scenarios controls";
    }

    @include bp("wide") {
      grid-template-columns: 1fr 2fr max-content;
      grid-template-areas: "tabs scenarios controls";
    }

    .tabs {
      margin-block-start: 0;
      margin-block-end: 0;
      flex-shrink: 1;
      gap: 0.25em;
      display: flex;
      justify-content: space-between;
      min-width: max-content;
      align-items: center;
      grid-area: tabs;
      padding: 0.5em;
      border-radius: 0.5em;
      border: solid 1px var(--surface-500);
      background-color: var(--surface-50);

      a {
        user-select: none;
        list-style: none;
        text-align: center;
        flex-grow: 1;
        padding: 0.25em 1em;
        border-radius: 0.25em;

        &.disabled {
          pointer-events: none;
          .menu-item {
            color: var(--surface-400);
          }
        }

        &:hover {
          outline: solid 1px var(--surface-500);
          text-decoration: none;
        }

        .menu-item {
          color: var(--text-color);
        }

        &.active {
          border-radius: 0.25em;
          background-color: var(--danger-500);
          .menu-item {
            color: white;
          }
        }
      }
    }
    .scenarios {
      grid-area: scenarios;
      min-width: 0;
      overflow: visible;
    }

    .scenario-switcher-wrap {
      flex: 1 1 0;
      min-width: 0;
      container-type: inline-size;
      container-name: scenario-switcher;
    }

    :deep(.scenario-switcher-trigger) {
      width: 100%;
      max-width: 100%;
    }

    :deep(.scenario-switcher-trigger .p-button-label) {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
    }

    .scenario-switcher-trigger-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .controls {
      grid-area: controls;
      flex: auto 1 0;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 0.25em;
      flex-wrap: wrap;
      @include bp("touch") {
        align-items: center;
        justify-content: space-between;
        flex-direction: row;
        flex-wrap: nowrap;
      }
      div,
      button {
        flex-basis: auto;
        flex-grow: 1;
        flex-shrink: 0;
      }
    }
  }
  .dual-stack-legend {
    position: relative;
    z-index: 3;
    padding: 0.25em 0.75em 0.5em;
    display: flex;
    justify-content: center;
    gap: 1rem;
    font-size: 0.8rem;
  }

  .dual-stack-legend .legend-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    border: 1px solid var(--surface-500);
    background-color: var(--surface-0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    font-weight: 500;
  }

  .dual-stack-legend .lower-pill {
    border-color: #10b981;
    background-color: rgba(16, 185, 129, 0.08);
    color: #047857;
  }

  .dual-stack-legend .upper-pill {
    border-color: #3b82f6;
    background-color: rgba(59, 130, 246, 0.08);
    color: #1d4ed8;
  }
  &.shrink {
    margin: 0;
    position: sticky;
    top: -1px;
    padding: 0.5em 3em;
  }
}
.only-rows {
  :deep(.panel-row) {
    padding: 1em;
    background: var(--surface-100);
    gap: 0.25em;
    grid-template-columns: repeat(auto-fill, minmax(150px, 100%));
    transition: border-color 0.2s ease-in-out;
  }

  :deep(.equipment-header) {
    // &.has-extra {
    //   background: lightcoral;
    //   grid-template-columns: minmax(20px, max-content) minmax(100px, 1fr), 1fr;
    // }

    .extra {
      grid-column: 1/-1;
      grid-row: 2;
    }
    .right {
      grid-column: 2/-1;
    }
  }
}

#rightSplitterPanel {
  overflow: initial !important;
}

.splitter-right {
  position: sticky;
  overflow-x: hidden;
  overflow-y: scroll;
  scrollbar-gutter: stable;
  padding: 1em;
}
.equipment-section {
  transition: all 0.2s ease-in-out;

  .hidden {
    position: absolute;
    display: none;
  }
  .scroll-label {
    cursor: pointer;
    user-select: none;
    padding-left: 0.325em;
    text-decoration: underline;
    transition: all 0.2s ease-in-out;

    &:hover {
      font-weight: 700;
    }
  }

  .calcrow {
    width: 100%;
    min-height: 100%;
    display: grid;
    column-gap: 1em;
    row-gap: 0.125em;
    grid-template-columns: repeat(auto-fit, minmax(min-content - 1em, 1fr));
    transition: all 0.2s ease-in-out;
    :deep(.convert-mode) {
      border: 1px solid var(--danger-500);
    }
  }
}

.schematic-wrapper {
  background: var(--surface-100);
  background-image: var(--gradient-vertical);
}

@media only screen and (max-width: 750px) {
  .wrapper {
    .scenario-header {
      display: initial;
      .tabs {
        min-width: initial;
        flex-wrap: wrap;
        margin-bottom: 14px;
      }
    }
    &.shrink {
      padding: 0;
    }
  }
  .flex {
    justify-content: center;
    margin-top: 5px;
  }
  .toggle {
    justify-content: center !important;
  }
}
.toggle {
  grid-area: controls;
  justify-content: flex-end;
}

// Dark mode: use lighter text colors for better readability
[data-theme="dark"] {
  .wrapper {
    .dual-stack-legend .lower-pill {
      color: #10b981; // Use the same green as border for better contrast
    }

    .dual-stack-legend .upper-pill {
      color: #60a5fa; // Use a lighter blue for better contrast
    }
  }
}
</style>

<style lang="scss">
/* OverlayPanel content is portaled to body — styles must not be scoped */
.scenario-reorder-panel {
  min-width: 280px;
  max-width: min(420px, 92vw);
  max-height: min(60vh, 480px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.scenario-reorder-list {
  overflow-y: auto;
  flex: 1 1 auto;
  padding: 0.25rem 0.25rem 0;
}

.scenario-reorder-actions {
  flex-shrink: 0;
}

.scenario-reorder-row-scenario {
  display: flex;
  align-items: center;
  padding: 0.45rem 0.35rem;
  border-radius: 6px;
  margin-bottom: 4px;
  border: 1px solid var(--surface-200, #e9ecef);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.scenario-reorder-row-scenario.is-dragging {
  position: relative;
  z-index: 2;
  background: var(--surface-100, #f1f5f9);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
  transform: scale(1.02);
}

.scenario-reorder-row-scenario.is-hover-target {
  background: rgba(59, 130, 246, 0.1);
  outline: 1px dashed var(--primary-color, #3b82f6);
}

.scenario-reorder-row-action {
  padding: 0.55rem 0.45rem;
  margin-top: 0.35rem;
  border-top: 1px solid var(--surface-300, #e2e8f0);
  cursor: pointer;
  color: var(--primary-color, #2563eb);
  font-size: 0.9rem;
}

.scenario-reorder-row-action:hover {
  background: var(--surface-50, #f8fafc);
}

.scenario-reorder-label {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  line-height: 1.35;
  padding-right: 0.25rem;
}

.drag-handle-grip {
  display: grid;
  grid-template-columns: repeat(2, 5px);
  grid-template-rows: repeat(3, 5px);
  gap: 2px;
  align-items: center;
  justify-items: center;
  padding: 4px;
  margin-right: 8px;
  flex-shrink: 0;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.drag-handle-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #64748b;
}
</style>
